import React from "react";

import { faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faExclamationTriangle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import styles from "./Card.module.css";

export function ConnectionLabel(props: {
	connected: boolean;
	hasErrors?: boolean;
	className?: string;
	disconnectedLabel?: string;
	connectedLabel?: string;
}) {
	const { connected, className, disconnectedLabel, connectedLabel, hasErrors = false } = props;
	if (connected) {
		return (
			<div
				className={cx(styles.connectionStatusLabel, styles.connected, className, {
					[styles.connectedWithErrors]: hasErrors,
				})}
			>
				{!hasErrors ? <FontAwesomeIcon icon={faCheck} className={styles.icon} /> : undefined}
				{connectedLabel ?? "Connected"}
			</div>
		);
	}
	return (
		<div className={cx(styles.connectionStatusLabel, styles.notConnected, className)}>
			{disconnectedLabel ?? "Not connected"}
		</div>
	);
}

type CardProps = {
	name: string;
	description: string;
	connected: boolean;
	logo: React.ReactNode;
	actions?: Array<React.ReactNode>;
	children?: React.ReactNode;
	connectedLabel?: string;
	disconnectedLabel?: string;
	className?: string;
	displayNameOnFooter?: boolean;
	showConnectedLabel?: boolean;
	noticeText?: string;
	warningText?: string;
	recommended?: boolean;
};

export default function Card({
	name,
	description,
	logo,
	actions,
	children,
	connectedLabel,
	disconnectedLabel,
	className,
	displayNameOnFooter,
	noticeText,
	warningText,
	recommended,
	...props
}: CardProps) {
	const showConnectedLabel = props.showConnectedLabel ?? true;

	const card = (
		<div className={cx(styles.card, className)}>
			<div>
				<div className={styles.header}>
					{!displayNameOnFooter && (
						<div>
							{name}
							{recommended && <span className={styles.recommended}>recommended</span>}
						</div>
					)}
					{actions && actions.length > 0 && (
						<Menu>
							<Menu.Trigger asChild>
								<IconButton icon={faEllipsisV} className={cx(styles.kebabMenu)} />
							</Menu.Trigger>
							<Menu.Content align="end">{actions}</Menu.Content>
						</Menu>
					)}
				</div>
				<div className={styles.description}>{description}</div>
				{children}
			</div>
			<div className={styles.footer}>
				<div style={{ display: "flex" }}>{logo}</div>
				{displayNameOnFooter && <span className={styles.footerName}>{name}</span>}
				<div className={styles.footerLabels}>
					{showConnectedLabel && (
						<div>
							<ConnectionLabel
								hasErrors={!!warningText}
								connectedLabel={connectedLabel}
								disconnectedLabel={disconnectedLabel}
								connected={props.connected}
							/>
						</div>
					)}
					{noticeText && (
						<Tooltip content={noticeText} maxWidth={250}>
							<div className={styles.noticeIconContainer}>
								<FontAwesomeIcon className={styles.noticeIcon} icon={faExclamationCircle} />
							</div>
						</Tooltip>
					)}
					{warningText && (
						<Tooltip content={warningText} maxWidth={250}>
							<div className={styles.warningIconContainer}>
								<FontAwesomeIcon className={styles.warningIcon} icon={faExclamationTriangle} />
							</div>
						</Tooltip>
					)}
				</div>
			</div>
		</div>
	);
	return card;
}
